<template>
    <div v-if="invoice && invoice.seller" class="invoice p-4 bg-white w-100" id="invoice">
        <div class="d-flex justify-content-between align-items-center mb-4" >
            <img :src="invoice.seller.logo" alt="Logo" height="100"  v-show="!showButtons"/>
            <div class="text-center" v-show="!showButtons">
                <div class="font-weight-bold text-uppercase" style="font-size: 1.75rem;">
                    {{ invoiceTypeLabel }}
                </div>
                <div class="font-weight-normal">
                    N° {{ invoice.number }} - {{ invoice.issued_at }}
                </div>
            </div>
            <div class="text-right">
                <qrcode-vue :value="invoice.seller.website" :size="70"  v-if="!showButtons"/><br>
                <div class="btn-group mt-2" v-if="showButtons">
                    <button v-if="invoice.status == 'Brouillon'" class="btn btn-sm btn-outline-primary" @click="$emit('edit-invoice')">
                        <i class="fas fa-edit mr-1"></i> Modifier
                    </button>
                    <!-- <button class="btn btn-sm btn-success" @click="$emit('confirm-invoice')">
                        <i class="fas fa-check mr-1"></i> Confirmer
                    </button> -->
                    <button class="btn btn-sm btn-dark" @click="$emit('print-invoice')">
                        <i class="fas fa-print mr-1"></i> Imprimer
                    </button>
                </div>
            </div>
        </div>

        <div class="row mb-4" v-show="!showButtons">
            <div class="col-md-6">
                <h5 class="font-weight-bold">De :</h5>
                <p class="mb-0">{{ invoice.seller.name }}</p>
                <p class="mb-0">{{ invoice.seller.email }}</p>
                <p class="mb-0">{{ invoice.seller.phone }}</p>
            </div>
            <div class="col-md-6 text-right">
                <h5 class="font-weight-bold">Client :</h5>
                <p class="mb-0">{{ invoice.client.name }}</p>
                <p class="mb-0">{{ invoice.client.address }}</p>
                <p class="mb-0">{{ invoice.client.phone }}</p>
            </div>
        </div>

        <table class="table table-bordered">
            <thead class="thead-light">
                <tr>
                    <th>Désignation</th>
                    <th class="text-right">Quantité</th>
                    <th class="text-right">Prix unitaire</th>
                    <th class="text-right">Montant</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, index) in invoice.items" :key="index">
                    <td>{{ item.title }}</td>
                    <td class="text-right">{{ item.qty }}</td>
                    <td class="text-right">{{ formatCurrency(item.price) }}</td>
                    <td class="text-right">{{ formatCurrency(item.qty * item.price) }}</td>
                </tr>
            </tbody>
        </table>

        <div v-for="(section, index) in invoice.description" :key="index" class="my-3">
            <h6 class="font-weight-bold">{{ section.title }}</h6>
            <p>{{ section.content }}</p>
        </div>

        <div class="text-right">
            <p><strong>Sous-total :</strong> {{ formatCurrency(subtotal) }}</p>
            <p v-if="options.useTVA"><strong>TVA ({{ invoice.taxes.tva }}%) :</strong> {{ formatCurrency(tva) }}</p>
            <p v-if="options.useTPS"><strong>TPS ({{ invoice.taxes.tps }}%) :</strong> {{ formatCurrency(tps) }}</p>
            <p v-if="options.useCSS"><strong>CSS ({{ invoice.taxes.css }}%) :</strong> {{ formatCurrency(css) }}</p>
            <h5 class="font-weight-bold">Total : {{ formatCurrency(total) }}</h5>
        </div>

        <div class="text-right mt-5" v-show="!showButtons">
            <!-- <p class="mb-0">Signature</p> -->
            <img :src="invoice.seller.signature" alt="Signature" height="100" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import QrcodeVue from 'qrcode.vue';

export default defineComponent({
    name: 'InvoiceRenderer',
    components: { QrcodeVue },
    props: {
        showButtons: {
            type: Boolean,
            default: true
        },
        invoice: {
            type: Object,
            required: true
        },
        options: {
            type: Object,
            default: () => ({
                showTaxes: true,
                useTVA: true,
                useTPS: false,
                useCSS: false,
                currency: 'XAF',
                locale: 'fr'
            })
        },
        type: {
            type: String,
            default: 'definitive'
        }
    },
    data() {
        return {};
    },
    computed: {
        invoiceTypeLabel(): string {
            return this.invoice.type === 'proforma' ? 'FACTURE PROFORMA' : 'FACTURE';
        },
        subtotal(): number {
            return this.invoice.items.reduce((acc: number, item: any) => acc + item.qty * item.price, 0);
        },
        tva(): number {
            return this.options.useTVA ? this.subtotal * (this.invoice.taxes.tva / 100) : 0;
        },
        tps(): number {
            return this.options.useTPS ? this.subtotal * (this.invoice.taxes.tps / 100) : 0;
        },
        css(): number {
            return this.options.useCSS ? this.subtotal * (this.invoice.taxes.css / 100) : 0;
        },
        total(): number {
            return this.subtotal + this.tva + this.tps + this.css;
        }
    },
    methods: {
        formatCurrency(amount: number): string {
            return new Intl.NumberFormat(this.options.locale, {
                style: 'currency',
                currency: this.options.currency
            }).format(amount);
        }
    }
});
</script>

<style scoped>
.invoice {
    max-width: 900px;
    margin: auto;
    font-size: 0.95rem;
    color: #333;
}
</style>